import React, { useState } from 'react';
import {
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { downloadReportEmailNotificationEvidence } from '../../../hooks/api/reportsApi';

const formatTimestamp = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? String(value)
    : parsed.toLocaleString();
};

const EmailNotificationEvidencePanel = ({
  reportId,
  runtimeFilters,
  evidence,
  loading,
  onNextPage,
  onError,
}) => {
  const [exporting, setExporting] = useState(false);
  const rows = Array.isArray(evidence?.rows) ? evidence.rows : [];
  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await downloadReportEmailNotificationEvidence({
        reportId,
        filters: runtimeFilters,
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `email-notification-evidence-${reportId}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      onError?.(
        error?.response?.data?.message ||
        'Lifecycle email evidence export failed.'
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <div>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Lifecycle Email Evidence
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Emails generated from lifecycle events that may not appear as
              ordinary status-change rows, including scheduled cancellations,
              revisions, retries, and activation outcomes.
            </Typography>
          </div>
          <Button
            size="small"
            variant="outlined"
            onClick={handleExport}
            disabled={exporting}
          >
            Export evidence
          </Button>
        </Stack>
        <TableContainer>
          <Table size="small" aria-label="Lifecycle Email Evidence">
            <TableHead>
              <TableRow>
                <TableCell>Attempted</TableCell>
                <TableCell>BAC</TableCell>
                <TableCell>Program</TableCell>
                <TableCell>Notice</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Billing End Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.sendId}>
                  <TableCell>{formatTimestamp(row.evidenceAt)}</TableCell>
                  <TableCell>{row.bac || '—'}</TableCell>
                  <TableCell>{row.programName || '—'}</TableCell>
                  <TableCell>
                    {row.attemptKind || row.noticeKind || '—'}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={row.status || 'unknown'} />
                  </TableCell>
                  <TableCell>{row.billingEndDate || '—'}</TableCell>
                </TableRow>
              ))}
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary">
                      {loading
                        ? 'Loading lifecycle email evidence…'
                        : 'No lifecycle email evidence in this window.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {evidence?.pagination?.hasMore && (
          <Button
            size="small"
            onClick={() => onNextPage?.(evidence.pagination.cursor)}
            disabled={loading}
          >
            Next evidence page
          </Button>
        )}
      </Stack>
    </Paper>
  );
};

export default EmailNotificationEvidencePanel;
